import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Globe, LogOut, Plus, Check, PiggyBank, UserRound } from 'lucide-react';
import { Avatar, Button, CategoryBadge, ListRow, Sheet, Spinner, toastErr, toastOk, List } from '@dd/ui';
import { CATEGORY_CONFIG, type Category } from '@dd/shared';
import { api, errCode, errMsg, type SessionDto } from '@/lib/api';
import { useSession } from '@/stores/session';
import { useShop } from '@/hooks/useShop';
import { useLang, useT } from '@/lib/i18n';
import { useSync } from '@/sync/status';
import { coarseFingerprint } from '@/lib/platform';

interface ShopItem { id: string; name: string; category: Category; kind: string; archived?: boolean; archivedAt?: string | null }

/** Tap the shop name: switch shop / profile, language, sign out (Bible 13.1). */
export function ShopSwitcher({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t, lang } = useT();
  const nav = useNavigate();
  const profile = useSession((s) => s.profile);
  const establish = useSession((s) => s.establish);
  const logout = useSession((s) => s.logout);
  const shop = useShop();
  const online = useSync((s) => s.online);
  const setLang = useLang((s) => s.setLang);
  const qc = useQueryClient();
  const [busy, setBusy] = useState<string | null>(null);
  const isOwner = profile?.role === 'owner';
  const shops = useQuery({ queryKey: ['shops'], queryFn: () => api.get<{ items: ShopItem[] }>('/shops'), enabled: open && isOwner && online, staleTime: 30_000 });

  const switchTo = async (id: string) => {
    if (id === shop.id) return onClose();
    setBusy(id);
    try {
      const dto = await api.post<SessionDto>('/auth/switch-shop', { shopId: id });
      await establish(dto, null);
      onClose();
      nav('/', { replace: true });
    } catch (e) { toastErr(errMsg(e, lang)); } finally { setBusy(null); }
  };

  const restore = async (id: string) => {
    setBusy(id);
    try {
      await api.post(`/shops/${id}/archive`, { archived: false });
      await qc.invalidateQueries({ queryKey: ['shops'] });
      toastOk(t('দোকান ফিরে এসেছে।', 'Shop restored.'));
    } catch (e) { toastErr(errCode(e) === 'shop_limit_reached' ? t('প্ল্যানে আর দোকানের জায়গা নেই। প্ল্যান পেজ থেকে বাড়তি দোকান নিন।', 'No free shop slot on your plan. Add a slot from the plan page.') : errMsg(e, lang)); } finally { setBusy(null); }
  };
  const archivedShops = shops.data?.items.filter((s) => (s.archived || s.archivedAt) && s.kind !== 'personal') ?? [];
  const hasPersonal = shops.data?.items.some((x) => x.kind === 'personal' && !x.archived && !x.archivedAt) ?? true;
  const openPersonal = async () => {
    setBusy('personal');
    try {
      const dto = await api.post<SessionDto>('/shops', { kind: 'personal', category: 'personal_savings', name: t('আমার হিসাব', 'My money'), fingerprint: coarseFingerprint() });
      await establish(dto, null);
      onClose();
      nav('/', { replace: true });
    } catch (e) { toastErr(errMsg(e, lang)); } finally { setBusy(null); }
  };

  return (
    <Sheet open={open} onClose={onClose} title={t('দোকান ও প্রোফাইল', 'Shop & profile')}>
      <div className="flex items-center gap-3 rounded-2xl bg-neutral-50 p-3">
        <Avatar name={profile?.name ?? '?'} size={44} src={profile?.photoUrl} />
        <div className="min-w-0">
          <div className="truncate font-bold text-ink">{profile?.name}</div>
          <div className="text-sm text-ink-3">{profile?.role === 'owner' ? t('মালিক', 'Owner') : profile?.role === 'manager' ? t('ম্যানেজার', 'Manager') : t('ক্যাশিয়ার', 'Cashier')}</div>
        </div>
      </div>
      {isOwner && (
        <div className="mt-4">
          <h3 className="mb-2 text-sm font-bold text-ink-3">{t('আপনার দোকান', 'Your shops')}</h3>
          <List>
            {(shops.data?.items.filter((s) => !s.archived && !s.archivedAt) ?? [{ id: shop.id, name: shop.name, category: shop.category, kind: 'business' }]).map((s) => (
              <ListRow key={s.id} leading={<CategoryBadge category={s.category} size={36} />} title={s.name} subtitle={lang === 'bn' ? CATEGORY_CONFIG[s.category]?.labelBn : CATEGORY_CONFIG[s.category]?.labelEn}
                trailing={busy === s.id ? <Spinner size={18} /> : s.id === shop.id ? <Check size={18} className="text-brand" /> : undefined} onClick={() => void switchTo(s.id)} />
            ))}
            {shops.isLoading && <div className="flex justify-center p-3"><Spinner /></div>}
          </List>
          {archivedShops.length > 0 && (
            <div className="mt-3">
              <h3 className="mb-2 text-sm font-bold text-ink-3">{t('বন্ধ করা দোকান', 'Closed shops')}</h3>
              <List>{archivedShops.map((s) => (
                <ListRow key={s.id} leading={<CategoryBadge category={s.category} size={36} />} title={s.name} subtitle={t('বন্ধ — ডেটা নিরাপদ', 'Closed — data is safe')}
                  trailing={busy === s.id ? <Spinner size={18} /> : <Button size="sm" variant="secondary" onClick={() => void restore(s.id)}>{t('ফিরিয়ে আনুন', 'Restore')}</Button>} />
              ))}</List>
            </div>
          )}
          <Button variant="outline" block className="mt-3" icon={<Plus size={18} />} onClick={() => { onClose(); nav('/shops/new'); }}>{t('নতুন দোকান তৈরি', 'Create a new shop')}</Button>
          {!hasPersonal && online && <Button variant="ghost" block className="mt-2" loading={busy === 'personal'} icon={<PiggyBank size={18} />} onClick={() => void openPersonal()}>{t('ব্যক্তিগত হিসাব খুলুন (ফ্রি)', 'Open your personal space (free)')}</Button>}
        </div>
      )}
      <div className="mt-4 overflow-hidden rounded-2xl bg-white">
        <ListRow leading={<Globe size={20} className="text-ink-3" />} title={t('ভাষা', 'Language')} trailing={
          <div className="flex gap-1 rounded-lg bg-neutral-100 p-0.5 text-sm font-semibold">
            {(['bn', 'en'] as const).map((l) => <button key={l} type="button" onClick={() => setLang(l)} className={`rounded-md px-3 py-1 ${lang === l ? 'bg-white text-brand shadow-sm' : 'text-ink-3'}`}>{l === 'bn' ? 'বাংলা' : 'EN'}</button>)}
          </div>} />
        <ListRow leading={<UserRound size={20} className="text-ink-3" />} title={t('সেটিংস', 'Settings')} chevron onClick={() => { onClose(); nav('/settings'); }} />
        <ListRow leading={<LogOut size={20} className="text-bad" />} title={t('লগআউট', 'Sign out')} danger onClick={() => void logout().then(onClose)} />
      </div>
    </Sheet>
  );
}
